import type { ServerFeatures } from '@stores/features';
import type {
  FeatureCTAConfig,
  FeatureCTAContext,
  FeatureInfoConfig,
} from './components/usageCard/types';
import {
  faCode,
  faPaperPlane,
  faShieldHalved,
  faGear,
  faUser,
  faHeadset,
} from '@fortawesome/pro-regular-svg-icons';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';

export const CATEGORY_ICONS: Record<string, IconDefinition> = {
  'Collect & Transform': faCode,
  Deliver: faPaperPlane,
  Govern: faShieldHalved,
  'Management and Security': faGear,
  Profiles: faUser,
  Support: faHeadset,
};

/** Per-feature CTA configuration (message and info popover overrides per state) */
type FeatureCTA = FeatureCTAConfig & {
  afterTrialEnds?: string;
  trialBadge?: string;
  info?: FeatureInfoConfig;
};

interface FeatureDefinition {
  name: string;
  description?: string;
  docsUrl?: string;
  category: string;
  subcategory?: string;
  ctas?: FeatureCTA;
  displayType?: 'boolean' | 'limit';
}

export function buildDefaultFeatureCTAs({
  featureName,
  currentPlan,
  targetPlan,
  currentLimit,
}: FeatureCTAContext): FeatureCTA {
  const revertLimitDescription = `Your ${featureName} limit will revert to ${currentLimit} (included in the ${currentPlan} plan). Any resource exceeding this limit will be frozen and retained for up to 1 year. Upgrade to ${targetPlan} plan to keep all resources active.`;

  return {
    default:
      'Upgrade to unlock higher limits and expanded capabilities, helping you scale faster and get more value from the platform.',
    locked: targetPlan
      ? `${featureName} is not available on the ${targetPlan} plan. Contact sales to enable this feature.`
      : `${featureName} is not available on the ${currentPlan} plan. Upgrade to enable this feature and expand your capabilities.`,
    trialDefault: `Your ${targetPlan} trial includes higher default limits for ${featureName}, giving you more flexibility to explore its full potential. Upgrade to ${targetPlan} any time to extend your usage limits.`,
    frozen: `Some ${featureName} are currently inactive due to plan limits. Upgrade to ${targetPlan} to restore full functionality.`,
    overLimit: `You have exceeded the ${featureName} limit for the ${currentPlan} plan. Upgrade to extend your limits.`,
    limitReached: `You have reached the ${featureName} limit for the ${currentPlan} plan. Upgrade to create more.`,
    trialAboveCurrentLimit: `You are using ${featureName} beyond the ${currentPlan} plan limits during your trial. Upgrade to ${targetPlan} to keep everything active after the trial ends.`,
    trialLimitReached: `You have reached the ${featureName} limit included in your trial. Need more capacity?`,
    trialIncluded: `${featureName} is included in your ${targetPlan} trial. Upgrade to ${targetPlan} to continue using it after the trial ends.`,
    trialBadge: `${featureName} is available during your ${targetPlan} trial. Upgrade to ${targetPlan} to keep full access after your trial ends.`,
    info: {
      trial: revertLimitDescription,
      trialIncluded: `Access to ${featureName} will be removed when your trial ends. Any resources you created will remain in your workspace, but may become inactive, limited in functionality, or revert to supported default configurations. Upgrade to the ${targetPlan} plan to keep full access.`,
    },
  };
}

const CATEGORY_COLLECT_TRANSFORM = 'Collect & Transform';
const CATEGORY_GOVERN = 'Govern';
const CATEGORY_MANAGEMENT_SECURITY = 'Management and Security';

export const featuresRegistry: Partial<Record<keyof ServerFeatures, FeatureDefinition>> = {
  TRANSFORMATIONS_LIMIT: {
    name: 'Transformations',
    description:
      'Transform event data in real time to clean, enrich, filter, or route it before delivery to downstream tools.',
    category: CATEGORY_COLLECT_TRANSFORM,
    ctas: {},
  },

  PYTHON_TRANSFORMATIONS: {
    name: 'Python Transformations',
    description:
      'Write transformations using Python to perform advanced logic, complex enrichment, and flexible data manipulation.',
    category: CATEGORY_COLLECT_TRANSFORM,
  },
  NOTIFICATIONS: {
    name: 'Alert Notifications',
    description:
      'Set global alert and thresholds for your Event Stream sources and destinations. You can manage alerts for each source or destination within their settings.',
    category: CATEGORY_MANAGEMENT_SECURITY,
    ctas: {
      info: {
        trialIncluded: `Access to Alert Notifications will be removed when your trial ends, and you will no longer receive notifications. Your configuration will be preserved for 1 year. Upgrade to keep full access.`,
      },
    },
  },

  EVENT_AUDIT_API: {
    name: 'Event Audit API',
    description:
      'Query event delivery logs programmatically to monitor data flow, troubleshoot issues, and verify delivery to destinations.',
    category: CATEGORY_GOVERN,
    ctas: {
      info: {
        trialIncluded: `When your trial ends, the Event Audit API will be disabled. You will no longer have access to event metadata or automatic Data Catalog imports. Your configuration will be kept for 1 year. Upgrade to keep full access.`,
      },
    },
  },

  WAREHOUSE_TUNNELLING: {
    name: 'Warehouse SSH Tunnels',
    description:
      'Securely connect to data warehouses through SSH tunnels when direct network access is restricted.',
    category: CATEGORY_MANAGEMENT_SECURITY,
  },

  MAX_TEAM_COUNT: {
    name: 'Team Members',
    description:
      'Invite team members and collaborate across engineering, product, and analytics teams with role-based access control.',
    category: CATEGORY_MANAGEMENT_SECURITY,
  },

  RETL_CONNECTIONS_LIMIT: {
    name: 'Reverse ETL Connections',
    description:
      'Sync modeled warehouse data to SaaS tools such as CRMs, marketing platforms, and support tools.',
    category: 'Deliver',
  },

  TRACKING_PLANS_LIMIT: {
    name: 'Tracking Plans',
    description:
      'Define schemas and validation rules to ensure consistent event structure and improve data quality across your pipeline.',
    category: CATEGORY_GOVERN,
  },

  TRACKING_PLAN_EVENTS_LIMIT: {
    name: 'Tracking Plan Events',
    description:
      'Define the events included in your tracking plan to control schema validation and maintain high data quality.',
    category: CATEGORY_GOVERN,
  },

  DATA_RETENTION: {
    name: 'Data Retention',
    description:
      'Control how long event data is stored for replay, debugging, auditing, and compliance requirements.',
    category: CATEGORY_MANAGEMENT_SECURITY,
  },

  DATA_RETENTION_STORAGE: {
    name: 'Data Retention',
    description:
      'Control how long event data is stored for replay, debugging, auditing, and compliance requirements.',
    category: CATEGORY_MANAGEMENT_SECURITY,
    ctas: {
      info: {
        trialIncluded:
          'Extended data retention will be removed when your trial ends. Event data will revert to the default 7-day retention period. Your configuration will be retained for 1 year.',
      },
    },
  },

  WORKSPACES_LIMIT: {
    name: 'Dev Workspace',
    displayType: 'boolean',
    description:
      'Create separate workspaces for development, testing, and production environments to safely manage changes.',
    category: CATEGORY_MANAGEMENT_SECURITY,
  },

  GROUPS: {
    name: 'Advanced Access Management',
    description:
      'Manage permissions using groups and roles to control access to sources, destinations, transformations, and settings.',
    category: CATEGORY_MANAGEMENT_SECURITY,
  },

  BOT_DETECTION: {
    name: 'Bot Detection',
    description:
      'Automatically identify bot traffic and prevent invalid events from affecting analytics accuracy.',
    category: CATEGORY_GOVERN,
    ctas: {
      trialIncluded:
        'Detect bot traffic automatically and prevent invalid events from impacting analytics and downstream tools.',
    },
  },

  BOT_EVENT_MANAGEMENT: {
    name: 'Bot Event Management',
    description:
      'Configure rules to flag or drop bot-generated events globally or per source to maintain clean analytics data.',
    docsUrl: 'https://www.rudderstack.com/docs/data-governance/bot-management/',
    category: CATEGORY_GOVERN,
    ctas: {
      locked:
        'Define rules to flag or drop bot events and maintain accurate analytics across your entire data pipeline.',
    },
  },
  TP_SOURCE_DROP_CONFIG: {
    name: 'Violation Config',
    description:
      'Configure rules to flag or drop bot-generated events globally or per source to maintain clean analytics data.',
    category: CATEGORY_GOVERN,
    ctas: {
      locked:
        'You can’t edit violation configuration settings. Upgrade your plan to unlock this feature.',
      info: {
        trialIncluded: `Access to Violation Config will be removed when your trial ends. You will no longer be able to edit violation configuration settings, and the current configuration will be reverted to default (drop events). Your configuration will be retained for 1 year.`,
      },
    },
  },
  CREDENTIALS: {
    name: 'Credentials',
    description:
      'The credential store is a central repository in the RudderStack dashboard for securely storing and efficiently managing your configuration data.',
    category: CATEGORY_MANAGEMENT_SECURITY,
    ctas: {
      info: {
        trialIncluded: `Access to Credentials will be removed when your trial ends. You will no longer be able to use stored secrets or variables. Your configuration will be retained for 1 year.`,
      },
    },
  },
};
